import type { SearchFilters } from '../../search/noteSearch'
import type { IconName } from '../../components/Icon'
import type { StudioDestination } from './DesktopSidebar'

export interface SearchFilterScopePresentation {
  title: string
  detail: string
  icon: IconName
}

const propertyLabels = {
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  article: 'Article',
  social: 'Social',
} as const

export function searchFilterScopePresentation(filters: SearchFilters): SearchFilterScopePresentation | null {
  const status = filters.status ?? 'all'
  const tags = [...new Set([
    ...(filters.tags ?? []),
    ...(filters.tag ? [filters.tag] : []),
  ])]
  const activeFilters = [
    status !== 'all',
    Boolean(filters.property),
    Boolean(filters.hasAlarm),
    Boolean(filters.quickAccess),
    tags.length > 0,
  ].filter(Boolean).length

  if (activeFilters === 0) return null
  if (activeFilters === 1) {
    if (filters.quickAccess) return { title: 'Quick Access notes', detail: 'Saved for quick access', icon: 'flash' }
    if (filters.hasAlarm) return { title: 'Notes with reminders', detail: 'Nearest reminder first', icon: 'bell' }
    if (status === 'archived') return { title: 'Archived notes', detail: 'Most recent first', icon: 'archive' }
    if (status === 'trash') return { title: 'Notes in trash', detail: 'Most recent first', icon: 'trash' }
    if (tags.length === 1) return { title: `Notes tagged “${tags[0]}”`, detail: 'Most recent first', icon: 'tag' }
    if (tags.length > 1) return { title: 'Notes with all tags', detail: tags.join(' · '), icon: 'tag' }
  }

  const detail = [
    filters.quickAccess ? 'Quick Access' : null,
    filters.hasAlarm ? 'Has reminder' : null,
    filters.property ? propertyLabels[filters.property] : null,
    status === 'archived' ? 'Archived' : status === 'trash' ? 'Deleted' : null,
    tags.length > 0 ? tags.join(' · ') : null,
  ].filter((label): label is string => Boolean(label)).join(' · ')

  return { title: 'Filtered notes', detail, icon: 'filter' }
}

// These are direct shortcuts to existing filters; combinations remain meaningful.
export function studioShortcutFilters(current: SearchFilters, destination: Exclude<StudioDestination, 'all'>): SearchFilters {
  if (destination === 'quick') return { ...current, quickAccess: !current.quickAccess }
  if (destination === 'reminders') return { ...current, hasAlarm: !current.hasAlarm }
  const status = destination === 'archive' ? 'archived' : 'trash'
  return { ...current, status: current.status === status ? 'all' : status }
}
